"use client";

import { useEffect } from "react";

import { ATTESA_MASSIMA_MS, QUIETE_MS, RIGHE_CON_ANCORA, quantoScorrere } from "./tieni-la-riga";

/**
 * **La riga resta dov'era sullo schermo dopo un gesto** — le regole e il perché
 * in `tieni-la-riga.ts`. Ascolta ogni invio di modulo: se il modulo sta dentro una
 * riga con un'ancora, ne ricorda l'altezza sullo schermo, e per qualche secondo —
 * finché arriva la risposta e il browser scorre all'ancora — la rimette lì.
 *
 * Smette appena la persona scorre da sé (rotella, dito, tastiera): la pagina è
 * sua. **Se la riga non c'è più** — la voce è uscita dall'elenco, e il ritorno
 * porta alla frase verde in cima — rimette la pagina allo scorrimento di prima:
 * al posto della riga c'è quella che veniva dopo. *Chiesto dal proprietario nello
 * stesso collaudo: «anche se la voce va via dall'elenco si può fare che la pagina
 * non si sposti?»*, sapendo che la frase verde resta fuori vista. Non disegna
 * niente.
 */
export function TieniLaRiga() {
  useEffect(() => {
    let ferma: (() => void) | null = null;

    function alInvio(evento: SubmitEvent) {
      const modulo = evento.target;
      if (!(modulo instanceof HTMLFormElement)) {
        return;
      }
      const riga = modulo.closest<HTMLElement>(RIGHE_CON_ANCORA);
      if (riga === null) {
        return;
      }
      ferma?.();
      const id = riga.id;
      const altoPrima = riga.getBoundingClientRect().top;
      const scorrimentoPrima = window.scrollY;
      const inizio = performance.now();
      let ultimaCorrezione = 0;
      let giro = 0;

      const smetti = () => {
        cancelAnimationFrame(giro);
        window.removeEventListener("wheel", smetti);
        window.removeEventListener("touchmove", smetti);
        window.removeEventListener("keydown", smetti);
        ferma = null;
      };
      ferma = smetti;
      window.addEventListener("wheel", smetti, { passive: true });
      window.addEventListener("touchmove", smetti, { passive: true });
      window.addEventListener("keydown", smetti);

      const guarda = (adesso: number) => {
        if (adesso - inizio > ATTESA_MASSIMA_MS || (ultimaCorrezione > 0 && adesso - ultimaCorrezione > QUIETE_MS)) {
          smetti();
          return;
        }
        const qui = document.getElementById(id);
        // La riga c'è: la si rimette alla sua altezza. Non c'è più: si rimette lo scorrimento di prima.
        const scorri =
          qui !== null
            ? quantoScorrere(altoPrima, qui.getBoundingClientRect().top)
            : quantoScorrere(scorrimentoPrima, window.scrollY) * -1;
        if (scorri !== 0) {
          window.scrollBy(0, scorri);
          ultimaCorrezione = adesso;
        }
        giro = requestAnimationFrame(guarda);
      };
      giro = requestAnimationFrame(guarda);
    }

    document.addEventListener("submit", alInvio, true);
    return () => {
      document.removeEventListener("submit", alInvio, true);
      ferma?.();
    };
  }, []);

  return null;
}
